import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { Alert, AlertAPIResponse, CreateAlertRequest } from '../../types';

// ==================== API CALLS ====================

// GET all alerts
const fetchAllAlerts = async (): Promise<Alert[]> => {
  const response = await apiClient.get<AlertAPIResponse[]>('/api/alert');
  return response.data;
};

// GET alert by ID
const fetchAlertById = async (id: number): Promise<Alert> => {
  const response = await apiClient.get<AlertAPIResponse>(`/api/alert/${id}`);
  return response.data;
};

// CREATE new alert
const createAlert = async (data: CreateAlertRequest): Promise<Alert> => {
  const response = await apiClient.post<AlertAPIResponse>('/api/alert', data);
  return response.data;
};

// UPDATE alert status (mark as read/unread)
const updateAlertStatus = async (id: number, status: 'RED' | 'UNREAD'): Promise<Alert> => {
  const response = await apiClient.patch<AlertAPIResponse>(
    `/api/alert/${id}?status=${status}`
  );
  return response.data;
};

// DELETE alert
const deleteAlert = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/alert/${id}`);
};

// MARK ALL ALERTS AS READ
const markAllAlertsAsRead = async (alertIds: number[]): Promise<void> => {
  // Mark all alerts as read by calling the update endpoint for each unread alert
  await Promise.all(
    alertIds.map(id => updateAlertStatus(id, 'RED'))
  );
};

// ==================== QUERY HOOKS ====================

// Fetch all alerts
export const useAlerts = () => {
  return useQuery({
    queryKey: ['alerts'],
    queryFn: fetchAllAlerts,
    staleTime: 30000, // Cache for 30 seconds
  });
};

// Fetch single alert by ID
export const useAlert = (id: number) => {
  return useQuery({
    queryKey: ['alerts', id],
    queryFn: () => fetchAlertById(id),
    enabled: !!id,
  });
};

// ==================== MUTATION HOOKS ====================

// Create alert
export const useCreateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
};

// Update alert status (mark as read/unread)
export const useUpdateAlertStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'RED' | 'UNREAD' }) =>
      updateAlertStatus(id, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alerts', data.id] });
    },
  });
};

// Mark all alerts as read
export const useMarkAllAlertsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllAlertsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
};

// Delete alert
export const useDeleteAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
};
